import { formatPickupScheduledAt, formatShippingAddressLines, type PickGroup } from "@gaole/shared";
import type { PickListOrderDetail } from "./types";

function pickSheetMeta(group: PickGroup): string {
  if (group.fulfilmentMethod === "PICKUP") {
    const location = escapeHtml(group.addressSummary);
    const pickupWhen = group.pickupScheduledAt
      ? `<div style="margin-top:3px;font-size:11px"><strong>Pickup date &amp; time:</strong> ${escapeHtml(formatPickupScheduledAt(group.pickupScheduledAt))}</div>`
      : "";
    const orderBadges = group.orders
      .map((o) => {
        const label = escapeHtml(o.orderNo);
        if (!o.pickupScheduledAt || o.pickupScheduledAt === group.pickupScheduledAt) return label;
        return `${label} <span style="font-weight:400;color:#444">(${escapeHtml(formatPickupScheduledAt(o.pickupScheduledAt))})</span>`;
      })
      .join(", ");
    return `
      <div><strong>Store pickup</strong></div>
      <div>${location}</div>
      ${pickupWhen}
      <div style="margin-top:4px"><strong>Orders:</strong> ${orderBadges}</div>
    `;
  }

  const addressLines = formatShippingAddressLines(group.shippingAddress)
    .map((line) => `<div>${escapeHtml(line)}</div>`)
    .join("");
  const orderBadges = group.orders.map((o) => escapeHtml(o.orderNo)).join(", ");
  return `
    ${addressLines}
    <div style="margin-top:8px"><strong>Orders:</strong> ${orderBadges}</div>
  `;
}

function packingSlipDestination(order: PickListOrderDetail): { heading: string; lines: string; pickupWhen?: string } {
  if (order.fulfilmentMethod === "PICKUP" && order.pickupDetails) {
    const details = order.pickupDetails;
    const lines = [
      details.name,
      details.phone,
      details.address1,
      details.address2,
      [details.city, details.postcode].filter(Boolean).join(", "),
      [details.state, details.country].filter(Boolean).join(", "),
      details.instructions,
    ]
      .filter((line): line is string => Boolean(line && String(line).trim()))
      .map((line) => `<div>${escapeHtml(line)}</div>`)
      .join("");
    return {
      heading: "Pickup at",
      lines,
      pickupWhen: formatPickupScheduledAt(details.scheduledAt),
    };
  }

  const addressLines = formatShippingAddressLines(order.shippingAddress)
    .map((line) => `<div>${escapeHtml(line)}</div>`)
    .join("");
  return { heading: "Ship to", lines: addressLines };
}

const PRINT_STYLES = `
  @page { margin: 14mm; }
  * { box-sizing: border-box; }
  body {
    font-family: Arial, Helvetica, sans-serif;
    color: #111;
    font-size: 11px;
    line-height: 1.35;
    margin: 0;
    padding: 0;
  }
  h1 { font-size: 15px; margin: 0 0 2px; }
  h2 { font-size: 11px; margin: 10px 0 4px; text-transform: uppercase; letter-spacing: 0.06em; }
  .meta { color: #444; margin-bottom: 8px; }
  .meta div { margin-bottom: 1px; }
  table { width: 100%; border-collapse: collapse; margin-top: 5px; }
  th, td { border: 1px solid #ccc; padding: 4px 6px; text-align: left; }
  th { background: #f5f5f5; font-size: 10px; }
  .qty { text-align: right; width: 56px; }
  .footer { margin-top: 12px; font-size: 9px; color: #666; }
`;

function escapeHtml(value: string): string {
  return value
    .replaceAll("&", "&amp;")
    .replaceAll("<", "&lt;")
    .replaceAll(">", "&gt;")
    .replaceAll('"', "&quot;");
}

function buildPrintDocument(title: string, bodyHtml: string): string {
  return `<!DOCTYPE html><html lang="en"><head><meta charset="utf-8"><title>${escapeHtml(title)}</title><style>${PRINT_STYLES}</style></head><body>${bodyHtml}</body></html>`;
}

/** Edge can print a blank page when iframe content is injected via document.write or printed before load. */
function printViaPopup(html: string): boolean {
  const win = window.open("", "_blank", "width=900,height=700");
  if (!win) return false;

  const triggerPrint = () => {
    win.focus();
    win.print();
  };

  win.document.open();
  win.document.write(html);
  win.document.close();

  if (win.document.readyState === "complete") {
    window.setTimeout(triggerPrint, 150);
  } else {
    win.addEventListener("load", () => window.setTimeout(triggerPrint, 150), { once: true });
  }

  return true;
}

function printViaIframe(html: string, title: string): boolean {
  const iframe = document.createElement("iframe");
  iframe.setAttribute("title", title);
  Object.assign(iframe.style, {
    position: "fixed",
    left: "-10000px",
    top: "0",
    width: "900px",
    height: "700px",
    border: "0",
  });
  document.body.appendChild(iframe);

  let finished = false;
  let printed = false;
  const cleanup = () => {
    if (finished) return;
    finished = true;
    iframe.remove();
  };

  const triggerPrint = () => {
    if (finished || printed) return;
    const win = iframe.contentWindow;
    const doc = win?.document;
    if (!win || !doc?.body?.innerHTML.trim()) {
      cleanup();
      printViaPopup(html);
      return;
    }

    printed = true;
    win.addEventListener("afterprint", cleanup, { once: true });
    window.setTimeout(cleanup, 60_000);
    win.focus();
    win.print();
  };

  iframe.addEventListener(
    "load",
    () => {
      window.setTimeout(triggerPrint, 100);
    },
    { once: true },
  );

  // srcdoc avoids document.write — more reliable in Edge than open/write/close.
  iframe.srcdoc = html;

  window.setTimeout(() => {
    if (finished) return;
    const doc = iframe.contentDocument;
    if (doc?.readyState === "complete" && doc.body?.innerHTML.trim()) {
      triggerPrint();
    }
  }, 800);

  return true;
}

function openPrintWindow(title: string, bodyHtml: string): boolean {
  const html = buildPrintDocument(title, bodyHtml);

  // Prefer popup on Edge — iframe print preview often stays about:blank there.
  const ua = navigator.userAgent;
  if (/Edg\//.test(ua)) {
    return printViaPopup(html) || printViaIframe(html, title);
  }

  return printViaIframe(html, title) || printViaPopup(html);
}

export function printPickGroupSheet(group: PickGroup, pickListCode: string) {
  const sheetTitle = group.fulfilmentMethod === "PICKUP" ? "Pickup sheet" : "Pick sheet";
  const rows = group.pickItems
    .map(
      (item) =>
        `<tr><td>${escapeHtml(item.name)}<br/><span style="font-size:9px;color:#666">${escapeHtml(item.sku)}</span></td><td class="qty">${item.quantity}</td></tr>`,
    )
    .join("");

  const html = `
    <h1>${sheetTitle} — ${escapeHtml(pickListCode)}</h1>
    <div class="meta">
      <div><strong>${escapeHtml(group.displayName)}</strong></div>
      <div>${escapeHtml(group.email)}</div>
      ${pickSheetMeta(group)}
    </div>
    <h2>Items to pick</h2>
    <table>
      <thead><tr><th>Product</th><th class="qty">Qty</th></tr></thead>
      <tbody>${rows}</tbody>
    </table>
    <div class="footer">Generated ${new Date().toLocaleString()}</div>
  `;
  return openPrintWindow(`${sheetTitle} ${pickListCode}`, html);
}

export function printAllPickGroupSheets(groups: PickGroup[], pickListCode: string) {
  const sections = groups
    .map((group) => {
      const sheetTitle = group.fulfilmentMethod === "PICKUP" ? "Pickup sheet" : "Pick sheet";
      const rows = group.pickItems
        .map(
          (item) =>
            `<tr><td>${escapeHtml(item.name)}<br/><span style="font-size:9px;color:#666">${escapeHtml(item.sku)}</span></td><td class="qty">${item.quantity}</td></tr>`,
        )
        .join("");
      return `
        <section style="page-break-after:always">
          <h1>${sheetTitle} — ${escapeHtml(pickListCode)}</h1>
          <div class="meta">
            <div><strong>${escapeHtml(group.displayName)}</strong></div>
            <div>${escapeHtml(group.email)}</div>
            ${pickSheetMeta(group)}
          </div>
          <h2>Items to pick</h2>
          <table>
            <thead><tr><th>Product</th><th class="qty">Qty</th></tr></thead>
            <tbody>${rows}</tbody>
          </table>
        </section>
      `;
    })
    .join("");
  return openPrintWindow(`Pick sheets ${pickListCode}`, sections);
}

export function printPackingSlip(order: PickListOrderDetail, pickListCode: string, companyName?: string) {
  const destination = packingSlipDestination(order);
  const rows = order.items
    .map((item) => `<tr><td>${escapeHtml(item.name)}</td><td class="qty">${item.quantity}</td></tr>`)
    .join("");
  const slipTitle = order.fulfilmentMethod === "PICKUP" ? "Pickup slip" : "Packing slip";

  const html = `
    ${companyName ? `<div style="font-weight:700;margin-bottom:12px">${escapeHtml(companyName)}</div>` : ""}
    <h1>${slipTitle}</h1>
    <div class="meta">
      <div><strong>Order:</strong> ${escapeHtml(order.orderNo)}</div>
      <div><strong>Pick list:</strong> ${escapeHtml(pickListCode)}</div>
      <div><strong>${destination.heading}:</strong></div>
      ${destination.lines}
      ${destination.pickupWhen ? `<div style="margin-top:3px;font-size:11px"><strong>Pickup date &amp; time:</strong> ${escapeHtml(destination.pickupWhen)}</div>` : ""}
    </div>
    <h2>Contents</h2>
    <table>
      <thead><tr><th>Item</th><th class="qty">Qty</th></tr></thead>
      <tbody>${rows}</tbody>
    </table>
    <div class="footer">Generated ${new Date().toLocaleString()}</div>
  `;
  return openPrintWindow(`${slipTitle} ${order.orderNo}`, html);
}

export function printAllPackingSlips(orders: PickListOrderDetail[], pickListCode: string, companyName?: string) {
  const sections = orders
    .map((order) => {
      const destination = packingSlipDestination(order);
      const rows = order.items
        .map((item) => `<tr><td>${escapeHtml(item.name)}</td><td class="qty">${item.quantity}</td></tr>`)
        .join("");
      const slipTitle = order.fulfilmentMethod === "PICKUP" ? "Pickup slip" : "Packing slip";
      return `
        <section style="page-break-after:always">
          ${companyName ? `<div style="font-weight:700;margin-bottom:12px">${escapeHtml(companyName)}</div>` : ""}
          <h1>${slipTitle}</h1>
          <div class="meta">
            <div><strong>Order:</strong> ${escapeHtml(order.orderNo)}</div>
            <div><strong>Pick list:</strong> ${escapeHtml(pickListCode)}</div>
            <div><strong>${destination.heading}:</strong></div>
            ${destination.lines}
            ${destination.pickupWhen ? `<div style="margin-top:3px;font-size:11px"><strong>Pickup date &amp; time:</strong> ${escapeHtml(destination.pickupWhen)}</div>` : ""}
          </div>
          <h2>Contents</h2>
          <table>
            <thead><tr><th>Item</th><th class="qty">Qty</th></tr></thead>
            <tbody>${rows}</tbody>
          </table>
        </section>
      `;
    })
    .join("");
  return openPrintWindow(`Packing slips ${pickListCode}`, sections);
}

export function printFailedToast() {
  return "Could not open print view — allow pop-ups for this site and try again";
}
